import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Clock, MapPin, Home, Briefcase, Monitor, AlertTriangle, Square } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import './MyAttendance.css';

const MyAttendance = () => {
  // Office location configuration - load from localStorage or use defaults
  const getSavedLocation = () => {
    const saved = localStorage.getItem('officeLocation');
    return saved ? JSON.parse(saved) : { lat: 35.819871, lng: 10.592260 }; // Default: Tunis, Tunisia
  };
  
  const getSavedRadius = () => {
    const saved = localStorage.getItem('allowedRadius');
    return saved ? parseInt(saved) : 100; // Default: 100 meters
  };

  const OFFICE_LOCATION = getSavedLocation();
  const ALLOWED_RADIUS = getSavedRadius();

  useMarkAsReadOnMount(['attendance']);

  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [clockStatus, setClockStatus] = useState('not_clocked_in');
  const [clockBusy, setClockBusy] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, checking, allowed, denied
  const [currentLocation, setCurrentLocation] = useState(null);
  // Remote work session in progress today ({ sessionId, clockedIn }), if any
  const [remoteSession, setRemoteSession] = useState(null);
  const [showStopRemote, setShowStopRemote] = useState(false);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.attendance)) return value.attendance;
    return [];
  };

  const normalizeDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  };

  const formatDateShort = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const parseTimeToMinutes = (value) => {
    if (!value) return null;
    const match = String(value).match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return (hours * 60) + minutes;
  };

  const isLeaveRecord = (record) => {
    const status = String(record?.status || '').toLowerCase();
    const notes = String(record?.notes || '').toLowerCase();
    return status === 'leave' || notes.includes('leave');
  };

  const enumerateDates = (startDate, endDate) => {
    const dates = [];
    const cursor = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    while (cursor <= end) {
      dates.push(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return dates;
  };

  // Geolocation helper functions
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          let message = 'Unable to get your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location access denied. Please enable location services.';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              message = 'Location request timed out.';
              break;
          }
          reject(new Error(message));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  };

  const isLocationWithinRange = (currentLocation) => {
    const distance = calculateDistance(
      OFFICE_LOCATION.lat,
      OFFICE_LOCATION.lng,
      currentLocation.lat,
      currentLocation.lng
    );
    
    console.log(`Distance from office: ${distance.toFixed(2)} meters`);
    return distance <= ALLOWED_RADIUS;
  };

  const checkRemoteSession = useCallback(async () => {
    try {
      const res = await api.get('/attendance/remote/status');
      const status = res?.status;
      setRemoteSession(status?.hasRecord && status.clockedIn && !status.clockedOut
        ? { sessionId: status.sessionId, clockedIn: status.clockedIn }
        : null);
    } catch {
      setRemoteSession(null);
    }
  }, []);

  useEffect(() => {
    checkRemoteSession();
  }, [checkRemoteSession]);

  const fetchAttendanceData = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
      const endDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-31`;

      const regularPromise = api.get('/attendance/me');
      const remotePromise = api.get(`/attendance/remote/history?startDate=${startDate}&endDate=${endDate}`);
      const absencesPromise = api.get('/absences/me');

      const [regularResult, remoteResult, absencesResult] = await Promise.allSettled([regularPromise, remotePromise, absencesPromise]);

      let records = [];

      if (regularResult.status === 'fulfilled') {
        const regularRecords = normalizeArray(regularResult.value).map((record) => ({
          ...record,
          date: normalizeDate(record.date),
        }));
        records = [...records, ...regularRecords.filter(r => !r.notes?.includes('Method: remote'))];
      } else {
        console.error('Regular attendance fetch failed:', regularResult.reason);
      }

      if (remoteResult.status === 'fulfilled') {
        const remoteData = remoteResult.value || {};
        const remoteHistory = Array.isArray(remoteData)
          ? remoteData
          : (Array.isArray(remoteData.history) ? remoteData.history : []);

        const remoteRecords = remoteHistory.map(record => ({
          id: record.id,
          date: normalizeDate(record.date),
          clockIn: record.clockIn,
          clockOut: record.clockOut,
          status: ['completed', 'active', 'auto_clocked_out'].includes(record.status) ? 'present' : 'absent',
          notes: `Method: remote, Session: ${record.sessionId}`,
          userId: record.userId,
          isRemote: true
        }));
        records = [...records, ...remoteRecords];
      } else {
        console.error('Remote attendance fetch failed:', remoteResult.reason);
      }

      if (absencesResult.status === 'fulfilled') {
        const absences = normalizeArray(absencesResult.value)
          .filter((a) => String(a?.status || '').toLowerCase() === 'approved');

        absences.forEach((absence) => {
          const start = normalizeDate(absence.startDate);
          const end = normalizeDate(absence.endDate);
          if (!start || !end) return;
          enumerateDates(start, end).forEach((date) => {
            const alreadyExists = records.some((r) => r.date === date && isLeaveRecord(r));
            if (alreadyExists) return;
            records.push({
              id: `leave-${absence.id}-${date}`,
              date,
              clockIn: null,
              clockOut: null,
              status: 'leave',
              notes: `Leave request #${absence.id}`,
              userId: absence.userId,
              isRemote: false,
            });
          });
        });
      }

      if (filter === 'regular') {
        records = records.filter((r) => isLeaveRecord(r) || !r.isRemote);
      } else if (filter === 'remote') {
        records = records.filter((r) => isLeaveRecord(r) || r.isRemote);
      } // 'all' keeps everything

      // Sort by date
      records.sort((a, b) => new Date(b.date) - new Date(a.date));
      setAttendance(records);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  }, [currentMonth, currentYear, filter]);

  const checkAttendanceStatus = useCallback(async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const records = await api.get('/attendance/me');
      const todayRecord = normalizeArray(records).find((r) => normalizeDate(r.date) === today);

      if (!todayRecord) {
        setClockStatus('not_clocked_in');
      } else if (todayRecord.clockIn && !todayRecord.clockOut) {
        setClockStatus('clocked_in');
      } else if (todayRecord.clockIn && todayRecord.clockOut) {
        setClockStatus('completed');
      } else {
        setClockStatus('not_clocked_in');
      }
    } catch (error) {
      console.error('Error checking attendance status:', error);
    }
  }, []);

  useEffect(() => {
    fetchAttendanceData();
    checkAttendanceStatus();
  }, [fetchAttendanceData, checkAttendanceStatus]);

  useEffect(() => {
    const timer = setInterval(() => {
      fetchAttendanceData();
      checkAttendanceStatus();
    }, 30000);
    return () => clearInterval(timer);
  }, [fetchAttendanceData, checkAttendanceStatus]);

  useEffect(() => {
    if (!message.text) return undefined;
    const timer = setTimeout(() => setMessage({ type: '', text: '' }), 2500);
    return () => clearTimeout(timer);
  }, [message]);

  const handleClockAction = async () => {
    setClockBusy(true);
    setLocationStatus('checking');
    setMessage({ type: '', text: '' });
    
    try {
      // Get current location
      const position = await getCurrentLocation();
      setCurrentLocation(position);
      
      // Check if within allowed radius
      const isWithinRange = isLocationWithinRange(position);
      
      if (!isWithinRange) {
        setLocationStatus('denied');
        setMessage({ 
          type: 'error', 
          text: `You are not within the allowed office perimeter (${position.accuracy?.toFixed(0)}m accuracy).` 
        });
        return;
      }
      
      setLocationStatus('allowed');
      
      // Proceed with clock in/out
      if (clockStatus === 'not_clocked_in') {
        const today = new Date().toISOString().slice(0, 10);
        const records = await api.get('/attendance/me');
        const todayRecord = normalizeArray(records).find((r) => normalizeDate(r.date) === today);
        if (todayRecord && isLeaveRecord(todayRecord)) {
          setShowLeaveWarning(true);
          return;
        }

        const now = new Date();
        const currentHour = now.getHours();
        let status = 'present';
        if (currentHour > 10 || (currentHour === 10 && now.getMinutes() > 15)) {
          status = 'late';
        }
        await api.post('/attendance/clock', {
          date: now.toISOString().slice(0, 10),
          clockIn: now.toTimeString().slice(0, 8),
          status,
        });
      } else if (clockStatus === 'clocked_in') {
        const now = new Date();
        await api.patch('/attendance/today', {
          clockOut: now.toTimeString().slice(0, 8),
        });
      }

      await checkAttendanceStatus();
      await fetchAttendanceData();
      setMessage({ 
        type: 'success', 
        text: clockStatus === 'clocked_in' ? 'Clock-out recorded.' : 'Clock-in recorded.' 
      });
    } catch (error) {
      setLocationStatus('denied');
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to process attendance action.' 
      });
    } finally {
      setClockBusy(false);
      // Reset location status after a delay
      setTimeout(() => setLocationStatus('idle'), 3000);
    }
  };

  const getClockButtonLabel = () => {
    if (clockBusy) return 'Processing...';
    if (clockStatus === 'clocked_in') return 'Clock Out';
    if (clockStatus === 'completed') return '✅ Completed';
    return 'Clock In';
  };

  const calculateWorkedHours = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return '-';

    const inTotalMinutes = parseTimeToMinutes(clockIn);
    const outTotalMinutes = parseTimeToMinutes(clockOut);
    if (inTotalMinutes === null || outTotalMinutes === null) return '-';
    
    const workedMinutes = outTotalMinutes - inTotalMinutes;
    
    if (workedMinutes <= 0) return '-';
    
    const hours = Math.floor(workedMinutes / 60);
    const minutes = workedMinutes % 60;
    
    return `${hours}h ${minutes}m`;
  };

  const calculateTotalWorkedHoursForDate = (recordsForDay) => {
    if (!recordsForDay.length) return '-';

    let totalMinutes = 0;
    recordsForDay.forEach((record) => {
      if (!record.clockIn || !record.clockOut) return;
      const inTotal = parseTimeToMinutes(record.clockIn);
      const outTotal = parseTimeToMinutes(record.clockOut);
      if (inTotal === null || outTotal === null) return;
      const worked = outTotal - inTotal;
      if (worked > 0) totalMinutes += worked;
    });

    if (!totalMinutes) return '-';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'present': 'badge-present',
      'late': 'badge-late',
      'absent': 'badge-absent',
      'leave': 'badge-leave'
    };
    return statusClasses[status] || 'badge-default';
  };

  const stopRemoteSession = async () => {
    if (!remoteSession) return;
    setShowStopRemote(false);
    try {
      const end = new Date();
      const start = new Date(`${end.toISOString().slice(0, 10)} ${remoteSession.clockedIn}`);
      const sessionDuration = Math.max(0, end - start);
      await api.post('/attendance/remote/clock-out', {
        sessionId: remoteSession.sessionId,
        clockOut: end.toTimeString().slice(0, 8),
        sessionDuration,
        activityStats: {
          sessionDuration,
          totalEvents: 0,
          totalActiveTime: sessionDuration,
          totalInactiveTime: 0,
          activityPercentage: '100',
          startTime: start.toISOString(),
          endTime: end.toISOString(),
        },
      });
      localStorage.removeItem('remoteAttendanceSession');
      setRemoteSession(null);
      setMessage({ type: 'success', text: 'Remote work session stopped and completed.' });
      fetchAttendanceData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error stopping the remote session: ' + (error.message || 'Unknown error') });
    }
  };

  if (loading) {
    return <div className="page-loading">Loading attendance records...</div>;
  }

  return (
    <div className="my-attendance-page">
      <div className="page-header">
        <h1><Clock size={24} /> My Attendance</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Location Status Indicator */}
          {locationStatus !== 'idle' && (
            <div className={`location-status ${locationStatus}`}>
              {locationStatus === 'checking' && <><MapPin size={14} /> Checking location...</>}
              {locationStatus === 'allowed' && <><MapPin size={14} /> Location verified</>}
              {locationStatus === 'denied' && <><MapPin size={14} /> Out of range</>}
            </div>
          )}
          
          <button
            className={`btn btn-primary ${locationStatus === 'denied' ? 'btn-danger' : ''}`}
            onClick={handleClockAction}
            disabled={clockBusy || clockStatus === 'completed'}
          >
            <MapPin size={16} /> {getClockButtonLabel()}
          </button>
          <button className="btn btn-primary" onClick={() => window.location.href = '/remote-work'}>
            <Monitor size={16} /> {remoteSession ? 'Remote Work in Progress' : 'Start Remote Work'}
          </button>
          {remoteSession && (
            <button
              className="btn btn-danger"
              onClick={() => setShowStopRemote(true)}
              title="Stop and complete the remote work session"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Square size={14} /> Stop
            </button>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showStopRemote}
        title="Stop remote work"
        message={`Stop and complete the remote work session started at ${remoteSession?.clockedIn || ''}?`}
        onConfirm={stopRemoteSession}
        onCancel={() => setShowStopRemote(false)}
        confirmText="Stop and complete"
        cancelText="Keep working"
      />

      {/* Location Information Display */}
      {currentLocation && (
        <div className="location-info">
          <div className="location-details">
            <span className="location-label">Current Location:</span>
            <span className="location-coords">
              {currentLocation.lat.toFixed(6)}, {currentLocation.lng.toFixed(6)}
            </span>
            <span className="location-accuracy">
              ±{currentLocation.accuracy?.toFixed(0)}m accuracy
            </span>
          </div>
          <div className="office-info">
            <span className="office-label">Office Location:</span>
            <span className="office-coords">
              {OFFICE_LOCATION.lat.toFixed(6)}, {OFFICE_LOCATION.lng.toFixed(6)}
            </span>
            <span className="radius-info">
              {ALLOWED_RADIUS}m radius
            </span>
          </div>
        </div>
      )}

      {message.text && <div className={`attendance-message ${message.type}`}>{message.text}</div>}

      {/* Attendance Rate Stats */}
      {(() => {
        // Filter attendance to current month
        const monthAttendance = attendance.filter(r => {
          const dateStr = String(r.date || '');
          const recordMonth = parseInt(dateStr.substring(5, 7), 10);
          const recordYear = parseInt(dateStr.substring(0, 4), 10);
          return recordMonth === currentMonth && recordYear === currentYear;
        });

        // Calculate workdays in the month (excluding weekends)
        const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
        let workdays = 0;
        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(currentYear, currentMonth - 1, day);
          const dayOfWeek = date.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude Saturday (6) and Sunday (0)
            workdays++;
          }
        }

        const authorizedLeaveDays = new Set(monthAttendance.filter(r => isLeaveRecord(r)).map(r => r.date)).size;
        const totalExpectedDays = workdays - authorizedLeaveDays;
        const presentLikeDays = new Set(monthAttendance.filter(r =>
          !isLeaveRecord(r) && ['present', 'late'].includes(String(r.status).toLowerCase())
        ).map(r => r.date)).size;
        const rate = totalExpectedDays > 0 ? ((presentLikeDays / totalExpectedDays) * 100).toFixed(1) : '0.0';

        return (
          <div className="attendance-stats">
            <div className="stat-card">
              <div className="stat-label">Attendance Rate</div>
              <div className="stat-value">{rate}%</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Days Present</div>
              <div className="stat-value">{presentLikeDays}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Expected Workdays</div>
              <div className="stat-value">{totalExpectedDays}</div>
            </div>
          </div>
        );
      })()}

      <div className="filters-bar">
        <div className="filter-group">
          <label>Month</label>
          <select value={currentMonth} onChange={(e) => setCurrentMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2024, i).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Year</label>
          <select value={currentYear} onChange={(e) => setCurrentYear(Number(e.target.value))}>
            {[2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Type</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="regular">Regular Work</option>
            <option value="remote">Remote Work</option>
          </select>
        </div>
      </div>

      <div className="attendance-calendar">
        <div className="calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="calendar-header-day">{day}</div>
          ))}
          
          {Array.from({ length: 35 }, (_, i) => {
            const day = i - new Date(currentYear, currentMonth - 1, 1).getDay() + 1;
            const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayRecords = attendance.filter((r) => r.date === dateStr);
            const dayRecord = dayRecords[0];
            const hasLeave = dayRecords.some((r) => isLeaveRecord(r));
            const hasRemote = dayRecords.some((r) => !isLeaveRecord(r) && r.isRemote);
            const today = new Date().toISOString().slice(0, 10);
            const isToday = dateStr === today;
            const isFutureDay = dateStr > today;
            const isValidDay = day > 0 && day <= new Date(currentYear, currentMonth, 0).getDate();
            const effectiveStatus = hasLeave
              ? 'leave'
              : (dayRecord?.status || (!isFutureDay ? 'absent' : ''));

            return (
              <div
                key={i}
                className={`calendar-day ${effectiveStatus ? getStatusBadge(effectiveStatus) : ''} ${isToday ? 'today' : ''} ${!isValidDay ? 'empty' : ''}`}
              >
                {isValidDay && (
                  <>
                    <span className="day-number">{day}</span>
                    {dayRecords.length > 0 && (
                      <div className="day-info">
                        {hasLeave ? null : hasRemote ? (
                          <Home size={12} className="remote-icon" />
                        ) : (
                          <Briefcase size={12} className="regular-icon" />
                        )}
                        <span className="hours">{calculateTotalWorkedHoursForDate(dayRecords)}</span>
                        {dayRecords.length > 1 && <span className="session-count-badge">{dayRecords.length} sessions</span>}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="attendance-list">
        <h2>Recent Records</h2>
        <div className="records-table-container">
          <table className="records-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th>Worked Hours</th>
                <th>Status</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {attendance.slice(0, 10).map(record => (
                <tr key={record.id}>
                  <td>{formatDateShort(record.date)}</td>
                  <td>{record.clockIn || '-'}</td>
                  <td>{record.clockOut || '-'}</td>
                  <td>{calculateWorkedHours(record.clockIn, record.clockOut)}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadge(record.status)}`}>
                      {record.status}
                    </span>
                  </td>
                  <td>
                    {isLeaveRecord(record) ? (
                      <span className="type-leave">Leave</span>
                    ) : record.isRemote ? (
                      <span className="type-remote"><Home size={12} /> Remote</span>
                    ) : (
                      <span className="type-regular"><Briefcase size={12} /> Regular</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showLeaveWarning && (
        <div className="attendance-warning-modal">
          <div className="attendance-warning-content">
            <AlertTriangle size={32} className="attendance-warning-icon" />
            <h3>Clock-In Blocked</h3>
            <p>You're on leave today — take a rest.</p>
            <button className="btn btn-primary" onClick={() => setShowLeaveWarning(false)}>
              Understood
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAttendance;
